import type { GeoItemPropLike } from './geoItemProps';
/**
 * Per-utility pothole measurements — shared web helpers (client + jobs).
 *
 * A deliberately disposable stop-gap ahead of the form-builder re-architecture
 * (MOB-203). All logic for the bespoke `utilityMeasurements` prop lives behind a
 * single shared field list so the whole feature can be removed in one PR. Mirrors
 * the mobile module of the same name.
 */

/** Prop label holding the `Utility` multi-select. */
export const UTILITY_SELECTION_LABEL = 'Utility' as const;
/** Prop label holding the structured per-utility `UtilityMeasurement[]`. */
export const UTILITY_MEASUREMENTS_LABEL = 'Utility Measurements' as const;
/** Prop `type` discriminator for the structured measurements prop. */
export const UTILITY_MEASUREMENTS_TYPE = 'utilityMeasurements' as const;

export type UtilityMeasurementEntry = {
  utility: string;
  depthToTop?: string;
  depthToBottom?: string;
  depth?: string;
  material?: string;
  size?: string;
  owner?: string;
};

export type UtilityMeasurementFieldKey = keyof Omit<UtilityMeasurementEntry, 'utility'>;

/**
 * The legacy single-value pothole measurement fields, paired with their
 * {@link UtilityMeasurementEntry} key and display label. The ONE shared source of
 * truth for the dual-write stamp, reconcile-on-display, and export formatting.
 */
export const LEGACY_UTILITY_MEASUREMENT_FIELDS: ReadonlyArray<{
  label: string;
  key: UtilityMeasurementFieldKey;
}> = [
  { label: 'Depth to Top', key: 'depthToTop' },
  { label: 'Depth to Bottom', key: 'depthToBottom' },
  { label: 'Depth', key: 'depth' },
  { label: 'Material', key: 'material' },
  { label: 'Size', key: 'size' },
  { label: 'Utility Owner', key: 'owner' },
];

/** Just the legacy field labels. */
export const LEGACY_UTILITY_FIELDS: string[] = LEGACY_UTILITY_MEASUREMENT_FIELDS.map((f) => f.label);

export type { GeoItemPropLike } from './geoItemProps';

/** Reads the structured `utilityMeasurements` entries, or `[]` when absent/malformed. */
export function getUtilityMeasurementEntries(
  props: readonly GeoItemPropLike[] | null | undefined,
): UtilityMeasurementEntry[] {
  if (!Array.isArray(props)) {
    return [];
  }
  const prop = props.find((p) => p?.label === UTILITY_MEASUREMENTS_LABEL);
  if (!prop || !Array.isArray(prop.value)) {
    return [];
  }
  return (prop.value as unknown[]).filter(
    (e): e is UtilityMeasurementEntry =>
      !!e &&
      typeof e === 'object' &&
      !Array.isArray(e) &&
      // `utility` is the one required field; mirror the mobile read guard so a
      // malformed entry (missing it, or non-string) is dropped on read.
      typeof (e as { utility?: unknown }).utility === 'string',
  );
}

/** Reads the legacy single-value snapshot as a partial entry. */
export function readLegacyMeasurementValues(
  props: readonly GeoItemPropLike[] | null | undefined,
): Omit<UtilityMeasurementEntry, 'utility'> {
  const result: Omit<UtilityMeasurementEntry, 'utility'> = {};
  if (!Array.isArray(props)) {
    return result;
  }
  for (const { label, key } of LEGACY_UTILITY_MEASUREMENT_FIELDS) {
    const prop = props.find((p) => p?.label === label);
    // Empty string counts as a value — a legacy client clearing a field must
    // propagate the clear through reconcile-on-display.
    if (prop && typeof prop.value === 'string') {
      result[key] = prop.value;
    }
  }
  return result;
}

/**
 * Reconcile-on-display. Two corrections for edits made by
 * legacy clients that don't know about the structured prop:
 *
 * 1. Prune entries whose utility is no longer in the `Utility` selection — a legacy
 *    client removes a utility by updating only the selection, leaving a stale entry.
 * 2. When the legacy single-value props differ from `entry[0]`, copy the legacy
 *    values into `entry[0]`.
 *
 * Returns the (pruned) entries unchanged when there's no mismatch (or none remain).
 * Dual-write guarantees `legacy == entry[0]` after every new-client save, so a
 * mismatch provably means an old mobile client edited the legacy fields.
 */
export function reconcileEntriesWithLegacy(
  props: readonly GeoItemPropLike[] | null | undefined,
): UtilityMeasurementEntry[] {
  // Prune first so the legacy single-values reconcile against the first surviving
  // selected utility.
  const entries = pruneEntriesToSelection(getUtilityMeasurementEntries(props), props);
  if (entries.length === 0) {
    return entries;
  }
  const legacy = readLegacyMeasurementValues(props);
  const entry0 = entries[0];
  const mismatch = LEGACY_UTILITY_MEASUREMENT_FIELDS.some(({ key }) => (legacy[key] ?? '') !== (entry0[key] ?? ''));
  if (!mismatch) {
    return entries;
  }
  const next = [...entries];
  next[0] = { ...entry0, ...legacy };
  return next;
}

/** Reads the selected `Utility` chip values (native string[] on both clients). */
export function getSelectedUtilities(props: readonly GeoItemPropLike[] | null | undefined): string[] {
  if (!Array.isArray(props)) {
    return [];
  }
  const prop = props.find((p) => p?.label === UTILITY_SELECTION_LABEL);
  const value = prop?.value;
  if (Array.isArray(value)) {
    // Drop empty-string entries — a stray blank selection would otherwise produce a
    // leading/trailing separator wherever this list gets joined (", Electric, Water").
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value];
  }
  return [];
}

/**
 * Prune-on-display: drop structured entries whose utility is no longer in the
 * `Utility` selection. A legacy client removes a utility by updating only the
 * selection (it can't touch the structured prop), leaving a stale entry the web app
 * would otherwise still render. No-op when the selection prop is ABSENT — there's no
 * safe basis to prune, and we must not nuke entries on data that lacks the prop. An
 * empty-but-present selection legitimately prunes to zero. Preserves entry order.
 */
export function pruneEntriesToSelection(
  entries: readonly UtilityMeasurementEntry[],
  props: readonly GeoItemPropLike[] | null | undefined,
): UtilityMeasurementEntry[] {
  const hasSelectionProp = Array.isArray(props) && props.some((p) => p?.label === UTILITY_SELECTION_LABEL);
  if (!hasSelectionProp) {
    return [...entries];
  }
  const selected = new Set(getSelectedUtilities(props));
  return entries.filter((e) => selected.has(e.utility));
}

/**
 * Derives the per-utility entries to render/seed a form from, mirroring the
 * mobile open-time logic:
 *
 * - structured prop present → reconcile `entry[0]` against the legacy props
 *   (so an old-mobile-client edit wins), then return.
 * - structured prop absent but utilities selected → migrate: seed one entry per
 *   selected utility from the legacy single values.
 * - otherwise → empty.
 */
export function deriveDisplayEntries(props: readonly GeoItemPropLike[] | null | undefined): UtilityMeasurementEntry[] {
  const existing = getUtilityMeasurementEntries(props);
  if (existing.length > 0) {
    return reconcileEntriesWithLegacy(props);
  }
  const selected = getSelectedUtilities(props);
  if (selected.length === 0) {
    return [];
  }
  const legacy = readLegacyMeasurementValues(props);
  return selected.map((utility) => ({ utility, ...legacy }));
}

/**
 * Reconciles the entries against a chip selection: append a blank entry for each
 * newly-selected utility, prune deselected ones, preserve still-selected data,
 * ordered to match `selectedUtilities`.
 */
export function syncEntriesWithSelection(
  entries: readonly UtilityMeasurementEntry[],
  selectedUtilities: readonly string[],
): UtilityMeasurementEntry[] {
  return selectedUtilities.map((utility) => entries.find((e) => e.utility === utility) ?? { utility });
}

/**
 * Dual-write stamp: returns `props` with the legacy single-value props synced to
 * `entry[0]` of the structured prop (clear-on-empty when zero entries). Props
 * without the structured prop are returned untouched. Mirrors mobile.
 */
export function applyUtilityMeasurementDualWrite<T extends GeoItemPropLike>(props: T[]): T[] {
  const measurementsProp = props.find((p) => p?.label === UTILITY_MEASUREMENTS_LABEL);
  if (!measurementsProp || !Array.isArray(measurementsProp.value)) {
    return props;
  }
  const entry0 = (measurementsProp.value as UtilityMeasurementEntry[])[0];
  return props.map((prop) => {
    const legacy = LEGACY_UTILITY_MEASUREMENT_FIELDS.find((f) => f.label === prop.label);
    if (!legacy) {
      return prop;
    }
    return { ...prop, value: entry0 ? (entry0[legacy.key] ?? '') : '' };
  });
}

/**
 * Serializes the entries into a readable per-utility breakdown for flat export
 * surfaces (KMZ placemark fields, PDF/print cells) that can't render a table.
 * e.g. `Electric — Depth to Top: 24, Material: Conduit; Water — Depth to Top: 48`.
 * Returns '' for empty input.
 */
export function formatUtilityMeasurementsForExport(
  entries: readonly UtilityMeasurementEntry[] | null | undefined,
): string {
  if (!Array.isArray(entries) || entries.length === 0) {
    return '';
  }
  return entries
    .map((entry) => {
      const parts = LEGACY_UTILITY_MEASUREMENT_FIELDS.map(({ label, key }) => {
        const value = entry[key];
        return value ? `${label}: ${value}` : null;
      }).filter(Boolean);
      return parts.length > 0 ? `${entry.utility} — ${parts.join(', ')}` : entry.utility;
    })
    .join('; ');
}

/** Legacy fields with their own flat column/field, i.e. all but `Depth`. */
export const PER_UTILITY_SUMMARY_FIELD_LABELS: readonly string[] = LEGACY_UTILITY_MEASUREMENT_FIELDS.filter(
  (f) => f.label !== 'Depth',
).map((f) => f.label);

/**
 * Positional per-utility breakdown for a single legacy field, e.g. `"Conduit, PVC"`
 * for `key: 'material'` with utilities `['Electric', 'Water']`.
 *
 * Order comes from the `Utility` chip selection, NOT the structured entries' own
 * storage order — the two can diverge (a utility can be selected with no entry yet,
 * or the edit dialog can select the same utility twice). A selected utility with no
 * matching entry, or an empty value for `key`, renders as the literal string
 * `'unset'` so every list stays aligned position-for-position with the `Utility`
 * column — `formatValue` only reformats non-`'unset'` values. `separator` is `', '`
 * for a single flat cell (PDF, KMZ) or `'\n'` for one-utility-per-line callers
 * (web table, CSV) that need to align with the `Utility` column's own line breaks.
 */
export function formatFieldByUtilityOrder(
  props: readonly GeoItemPropLike[] | null | undefined,
  key: UtilityMeasurementFieldKey,
  formatValue: (value: string) => string = (v) => v,
  separator = ', ',
): string {
  const selected = getSelectedUtilities(props);
  if (selected.length === 0) {
    return '';
  }
  const entries = deriveDisplayEntries(props);
  return selected
    .map((utility) => {
      const value = entries.find((e) => e.utility === utility)?.[key];
      return value ? formatValue(value) : 'unset';
    })
    .join(separator);
}

/**
 * Resolves the display/export value for a legacy single-value field shared across
 * the pothole table, CSV, PDF summary table, and KMZ export.
 *
 * Whenever at least one utility is selected, this is always a per-utility breakdown
 * ({@link formatFieldByUtilityOrder}) — mirroring the flat legacy value across every
 * selected utility rather than returning it bare, even without structured data. Only
 * falls back to the raw flat value when no utility is selected (no per-utility axis
 * to align to). `formatValue` applies on both paths so a suffix (e.g. PDF's `''`)
 * behaves identically either way.
 */
export function resolveLegacyFieldValue(
  props: readonly GeoItemPropLike[] | null | undefined,
  label: string,
  formatValue: (value: string) => string = (v) => v,
  separator = ', ',
): string {
  const flatProp = Array.isArray(props) ? props.find((p) => p?.label === label) : undefined;
  const flatValue = typeof flatProp?.value === 'string' ? flatProp.value : '';

  const field = LEGACY_UTILITY_MEASUREMENT_FIELDS.find((f) => f.label === label);
  if (!field || getSelectedUtilities(props).length === 0) {
    return flatValue ? formatValue(flatValue) : '';
  }
  return formatFieldByUtilityOrder(props, field.key, formatValue, separator);
}
